import { useCallback, useState, useSyncExternalStore } from "react";

/**
 * HOW BIG THE KITCHEN BOARD DRAWS — a per-DEVICE setting (A6, T16).
 *
 * The board used to pick its sizes from the pointer alone: a coarse pointer
 * got the tablet sizes, a mouse the design's desk sizes. A 50″ screen on the
 * wall above the pass has a fine pointer (a mouse nobody touches, or none at
 * all) and is read from two metres away, so it got the SMALLEST type of the
 * three. The size is therefore a choice made on the device, stored the same
 * way and for the same reason as the remembered station (`stationPreference`):
 *
 *  - `auto`  — today's behaviour: a coarse pointer reads as `touch`, a mouse
 *              keeps the design's sizes (no attribute at all);
 *  - `touch` — the tablet sizes, whatever the pointer says;
 *  - `wall`  — the distance sizes (`_paper-kitchen.css`, plan §4.12).
 *
 * It is applied as `data-kitchen-size` on the board's root, and only CSS reads
 * it: no component branches on the size.
 */
export type KitchenSizeChoice = "auto" | "touch" | "wall";
/** What the board root carries; `null` = the design's desk sizes. */
export type KitchenSize = "touch" | "wall" | null;

export const KITCHEN_SIZE_STORAGE_KEY = "triotyKitchenSize";
export const KITCHEN_SIZE_CHOICES: readonly KitchenSizeChoice[] = ["auto", "touch", "wall"];

const COARSE_POINTER = "(pointer: coarse)";

export function isKitchenSizeChoice(value: unknown): value is KitchenSizeChoice {
  return value === "auto" || value === "touch" || value === "wall";
}

/** The stored choice; anything unreadable or unknown is `auto`. */
export function readKitchenSizeChoice(): KitchenSizeChoice {
  // Wrapped like every storage read here: a private window or blocked site
  // data THROWS on access, and the board must still paint.
  try {
    const value = localStorage.getItem(KITCHEN_SIZE_STORAGE_KEY);
    return isKitchenSizeChoice(value) ? value : "auto";
  } catch {
    return "auto";
  }
}

/** The attribute a choice resolves to on this device. */
export function resolveKitchenSize(choice: KitchenSizeChoice, coarsePointer: boolean): KitchenSize {
  if (choice === "auto") return coarsePointer ? "touch" : null;
  return choice;
}

function subscribeCoarse(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const query = window.matchMedia(COARSE_POINTER);
  // Older engines only have the deprecated listener API.
  if (typeof query.addEventListener === "function") {
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }
  query.addListener?.(onChange);
  return () => query.removeListener?.(onChange);
}

function readCoarse(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(COARSE_POINTER).matches === true;
}

/** Whether this device's primary pointer is a finger (a tablet). */
export function useCoarsePointer(): boolean {
  return useSyncExternalStore(subscribeCoarse, readCoarse, () => false);
}

/** The device's size choice, its setter, and the attribute it resolves to. */
export function useKitchenSize(): {
  choice: KitchenSizeChoice;
  setChoice: (next: KitchenSizeChoice) => void;
  size: KitchenSize;
} {
  const [choice, setStored] = useState<KitchenSizeChoice>(readKitchenSizeChoice);
  const coarse = useCoarsePointer();
  const setChoice = useCallback((next: KitchenSizeChoice) => {
    setStored(next);
    try {
      localStorage.setItem(KITCHEN_SIZE_STORAGE_KEY, next);
    } catch {
      // An unwritable store still honours the choice for this session.
    }
  }, []);
  return { choice, setChoice, size: resolveKitchenSize(choice, coarse) };
}
